import React, { useState, useEffect } from"react";
import {
 FileText,
 Calendar,
 CheckCircle,
 Clock,
 XCircle,
 Filter,
} from"lucide-react";
import { useNavigate } from"react-router-dom";
import {
 Card,
 CardHeader,
 CardTitle,
 CardContent,
 Toast,
 ToastType,
 ModernButton,
 ListPageSkeleton,
} from"../../components/ui";
import { apiService } from"../../services/api";
import { Invoice } from"../../types";
import { formatCurrency } from"../../utils/formatters";
import { DatePicker } from"../../components/form/DatePicker";

export const InvoicesPage: React.FC = () => {
 const navigate = useNavigate();

 // Helper function to get first day of current month
 const getFirstDayOfMonth = () => {
 const now = new Date();
 return new Date(now.getFullYear(), now.getMonth(), 1)
 .toISOString()
 .split("T")[0];
 };

 // Helper function to get today's date
 const getToday = () => {
 return new Date().toISOString().split("T")[0];
 };

 const [invoices, setInvoices] = useState<Invoice[]>([]);
 const [isLoading, setIsLoading] = useState(true);
 const [toast, setToast] = useState<{
 message: string;
 type: ToastType;
 } | null>(null);
 const [page, setPage] = useState(1);
 const [totalPages, setTotalPages] = useState(1);
 const [filters, setFilters] = useState({
 startDate: getFirstDayOfMonth(),
 endDate: getToday(),
 });

 useEffect(() => {
 fetchInvoices();
 }, [page, filters]);

 const fetchInvoices = async () => {
 try {
 setIsLoading(true);
 const response = await apiService.getInvoices({ page, limit: 50 });
 let filteredInvoices = response.invoices || [];

 // Apply date filters
 if (filters.startDate) {
 filteredInvoices = filteredInvoices.filter(
 (invoice: Invoice) =>
 new Date(invoice.issueDate) >= new Date(filters.startDate)
 );
 }
 if (filters.endDate) {
 filteredInvoices = filteredInvoices.filter(
 (invoice: Invoice) =>
 new Date(invoice.issueDate) <= new Date(filters.endDate)
 );
 }

 setInvoices(filteredInvoices);
 setTotalPages(response.pagination?.totalPages || 1);
 } catch (err) {
 console.error("Erro ao carregar faturas:", err);
 setToast({
 message:"Erro ao carregar faturas",
 type:"error",
 });
 } finally {
 setIsLoading(false);
 }
 };

 const getStatusIcon = (status: Invoice["status"]) => {
 switch (status) {
 case"paid":
 return <CheckCircle className="h-5 w-5 text-green-600"/>;
 case"issued":
 return <Clock className="h-5 w-5 text-blue-600"/>;
 case"overdue":
 return <XCircle className="h-5 w-5 text-red-600"/>;
 case"draft":
 return <FileText className="h-5 w-5 text-muted-foreground"/>;
 default:
 return <FileText className="h-5 w-5 text-muted-foreground"/>;
 }
 };

 const getStatusLabel = (status: Invoice["status"]) => {
 const labels: Record<Invoice["status"], string> = {
 draft:"Rascunho",
 issued:"Emitida",
 paid:"Paga",
 overdue:"Vencida",
 cancelled:"Cancelada",
 };
 return labels[status] || status;
 };

  return (
 <div className="space-y-6">
 <div className="flex items-center justify-between">
 <h1 className="text-2xl font-bold text-foreground font-display">
 Faturas
 </h1>
 </div>

 {/* Filters */}
 <Card>
 <CardHeader>
 <CardTitle className="flex items-center">
 <Filter className="h-5 w-5 mr-2"/>
 Filtros
 </CardTitle>
 </CardHeader>
 <CardContent>
 <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
 <div>
 <DatePicker
 label="Data Inicial"
 value={filters.startDate}
 onChange={(value) =>
 setFilters({ ...filters, startDate: value })
 }
 placeholder="Selecione a data inicial"
 />
 </div>
 <div>
 <DatePicker
 label="Data Final"
 value={filters.endDate}
 onChange={(value) => setFilters({ ...filters, endDate: value })}
 placeholder="Selecione a data final"
 />
 </div>
 <div className="flex items-end">
 <ModernButton
 variant="outline"
 onClick={() =>
 setFilters({
 startDate: getFirstDayOfMonth(),
 endDate: getToday(),
 })
 }
 className="w-full"
 >
 Limpar Filtros
 </ModernButton>
 </div>
 </div>
 </CardContent>
 </Card>

 <Card>
 <CardHeader>
 <CardTitle>Lista de Faturas</CardTitle>
 </CardHeader>
 <CardContent>
 {isLoading ? (
 <ListPageSkeleton variant="table" rows={6} showFilters={false} />
 ) : invoices.length === 0 ? (
 <p className="text-sm text-muted-foreground">
 Nenhuma fatura encontrada
 </p>
 ) : (
 <div className="space-y-3">
 {invoices.map((invoice) => {
 const totalAmount = parseFloat(invoice.totalAmount.toString());
 const paidAmount = parseFloat(invoice.paidAmount.toString());
 const remaining = totalAmount - paidAmount;

 return (
 <div
 key={invoice.id}
 className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-surface cursor-pointer"
 onClick={() =>
 navigate(`/financial/invoices/${invoice.id}`)
 }
 >
 <div className="flex items-center gap-4 flex-1">
 {getStatusIcon(invoice.status)}
 <div className="flex-1">
 <div className="flex items-center gap-2">
 <h3 className="font-semibold text-foreground">
 {invoice.invoiceNumber}
 </h3>
 <span
 className={`px-2 py-1 text-xs rounded-full ${
 invoice.status ==="paid"
 ?"bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
 : invoice.status ==="overdue"
 ?"bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
 : invoice.status ==="issued"
 ?"bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
 :"bg-surface text-foreground dark:text-muted-foreground"
 }`}
 >
 {getStatusLabel(invoice.status)}
 </span>
 </div>
 <div className="mt-1 text-sm text-muted-foreground">
 <div className="flex items-center gap-4">
 <span>
 <Calendar className="h-3 w-3 inline mr-1"/>
 Vencimento:{""}
 {new Date(invoice.dueDate).toLocaleDateString(
"pt-BR"
 )}
 </span>
 {invoice.sale && (
 <span>
 Venda: {invoice.sale.vehicle?.brand?.name}{""}
 {invoice.sale.vehicle?.model?.name}
 </span>
 )}
 </div>
 </div>
 </div>
 </div>
 <div className="text-right">
 <div className="font-semibold text-foreground">
 {formatCurrency(totalAmount)}
 </div>
 {paidAmount > 0 && (
 <div className="text-sm text-muted-foreground">
 Pago: {formatCurrency(paidAmount)}
 </div>
 )}
 {remaining > 0 && (
 <div className="text-sm text-amber-600 dark:text-amber-400">
 Restante: {formatCurrency(remaining)}
 </div>
 )}
 </div>
 </div>
 );
 })}
 </div>
 )}

 {totalPages > 1 && (
 <div className="flex items-center justify-center gap-2 mt-4">
 <ModernButton
 variant="outline"
 onClick={() => setPage((p) => Math.max(1, p - 1))}
 disabled={page === 1}
 >
 Anterior
 </ModernButton>
 <span className="text-sm text-muted-foreground">
 Página {page} de {totalPages}
 </span>
 <ModernButton
 variant="outline"
 onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
 disabled={page === totalPages}
 >
 Próxima
 </ModernButton>
 </div>
 )}
 </CardContent>
 </Card>

 {toast && (
 <Toast
 message={toast.message}
 type={toast.type}
 onClose={() => setToast(null)}
 duration={3000}
 />
 )}
 </div>
 );
};
